import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Controller, useForm, useWatch, type Control, type FieldPath } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  ProviderSurveyResponseService,
  type PatchedProviderSurveyResponse,
  type ProviderSurveyResponse,
  RecommendationStatusEnum,
  RelationshipLengthEnum,
  RelationshipTypeEnum,
} from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Avatar } from '../../../components/avatar';
import { Button } from '../../../components/button';
import { CheckboxGroup, CheckboxField, Checkbox } from '../../../components/checkbox';
import { Fieldset, Field, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { RadioGroup, RadioField, Radio } from '../../../components/radio';
import { Section } from '../../../components/section';
import { SectionHeader } from '../../../components/SectionHeader';
import { Text } from '../../../components/text';
import { Textarea } from '../../../components/textarea';
import { reactSelectReset } from '../../../utils/utils';
import ProviderSurveyThankYou from './ProviderSurveyThankYou';

const LIKERT_OPTIONS = [
  { label: 'N/A', value: 0 },
  { label: 'Poor', value: 1 },
  { label: 'Fair', value: 2 },
  { label: 'Good', value: 3 },
  { label: 'Excellent', value: 4 },
];

const LIKERT_OPTIONS_NO_NA = [
  { label: 'Poor', value: 1 },
  { label: 'Fair', value: 2 },
  { label: 'Good', value: 3 },
  { label: 'Excellent', value: 4 },
];

const KNOWN_YEARS_OPTIONS = [
  { value: RelationshipLengthEnum.LESS_THAN_ONE_YEAR, label: 'Less than 1 year' },
  { value: RelationshipLengthEnum.ONE_TO_TWO_YEARS, label: '1-2 years' },
  { value: RelationshipLengthEnum.THREE_TO_FIVE_YEARS, label: '3-5 years' },
  { value: RelationshipLengthEnum.SIX_TO_TEN_YEARS, label: '6-10 years' },
  { value: RelationshipLengthEnum.TEN_PLUS_YEARS, label: 'More than 10 years' },
];

const CAPACITY_OPTIONS = [
  { value: RelationshipTypeEnum.CO_WORKER, label: 'Co-worker' },
  { value: RelationshipTypeEnum.PEER, label: 'Peer' },
  { value: RelationshipTypeEnum.SUPERVISOR, label: 'Supervisor' },
  { value: RelationshipTypeEnum.MANAGEMENT, label: 'Management' },
  { value: RelationshipTypeEnum.FELLOWSHIP, label: 'Fellowship' },
  { value: RelationshipTypeEnum.RESIDENCY, label: 'Residency' },
  { value: RelationshipTypeEnum.MEDICAL_SCHOOL, label: 'Medical School' },
  { value: RelationshipTypeEnum.OTHER, label: 'Other' },
];

const BROAD_ATTRIBUTES_OPTIONS: Record<RecommendationStatusEnum, string[]> = {
  [RecommendationStatusEnum.HIGHLY_RECOMMEND]: [
    'Clinical Excellence',
    'Reliable',
    'Communicates Well',
    'Team Player',
    'Excellent Leader',
    'Calm Under Pressure',
    'Ethical',
    'Procedurally Skilled',
    'Efficient and Competent',
    'Builds Patient Trust',
  ],
  [RecommendationStatusEnum.RECOMMEND_WITH_HESITATION]: [
    'Inconsistent Reliability',
    'Workload Management Issues',
    'Limited Procedures',
    'Communication Gaps',
    'Adapts Slowly',
    'Documentation Challenges',
    'Best in Low-Acuity',
    'Needs Oversight',
    'Unclear Decision-Making',
    'Passive Engagement',
  ],
  [RecommendationStatusEnum.DO_NOT_RECOMMEND]: [
    'Patient Safety Risk',
    'Unprofessional Behavior',
    'No-Shows or Late',
    'Poor Clinical Judgement',
    'Ignores Protocol',
    'Team Disruption',
    'Poor/Late Documentation',
    'License Concerns',
    'Extremely Slow',
    'Unreceptive to Feedback',
  ],
};

const nullableLikert = z.number().int().min(0).max(4).nullable();

// TODO: since we're allowing partial submits and the form controls don't really need type or value
// validation, do we really need a zod schema here?
const providerSurveySchema = z.object({
  // Nature of Relationship
  relationship_length: z.string().nullable(),
  relationship_type: z.string().nullable(),
  other_capacity: z.string().optional().default(''),

  // Personal Qualifications
  interpersonal_skills: nullableLikert,
  communication: nullableLikert,
  empathy: nullableLikert,
  reliability_and_dependability: nullableLikert,

  // Professional Qualifications
  clinical_skills: nullableLikert,
  quality_of_patient_care: nullableLikert,
  teamwork: nullableLikert,
  physical_and_mental_health: nullableLikert,

  // Ethical Standards
  integrity: nullableLikert,
  honesty: nullableLikert,
  adherence_to_ethical_guidelines: nullableLikert,

  // Recommendation Status (use API enum directly)
  recommendation_status: z.nativeEnum(RecommendationStatusEnum).nullable(),

  // Endorsement of Broad Attributes
  endorsements: z.array(z.string()).default([]),

  // Additional Comments (Optional)
  comments: z.string().optional().default(''),

  // Response Visibility
  response_visibility: z.enum(['yes', 'no']).nullable(),
});

type ProviderSurveyFormData = z.infer<typeof providerSurveySchema>;

const DEFAULT_VALUES: ProviderSurveyFormData = {
  relationship_length: null,
  relationship_type: null,
  other_capacity: '',
  interpersonal_skills: null,
  communication: null,
  empathy: null,
  reliability_and_dependability: null,
  clinical_skills: null,
  quality_of_patient_care: null,
  teamwork: null,
  physical_and_mental_health: null,
  integrity: null,
  honesty: null,
  adherence_to_ethical_guidelines: null,
  recommendation_status: null,
  endorsements: [],
  comments: '',
  response_visibility: null,
};

const LikertRow: React.FC<{
  name: FieldPath<ProviderSurveyFormData>;
  label: string;
  control: Control<ProviderSurveyFormData>;
  options?: Array<{ label: string; value: number }>;
  isLast?: boolean;
}> = ({ name, label, control, options = LIKERT_OPTIONS, isLast = false }) => {
  return (
    <div
      className={`tw:flex tw:flex-col tw:gap-2 tw:pb-4 ${!isLast ? 'tw:border-b tw:border-zinc-200 tw:dark:border-white/10' : ''}`}
    >
      <div className="tw:font-medium tw:dark:text-white">{label}</div>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <RadioGroup
            value={field.value === null ? undefined : String(field.value)}
            onChange={(val) => field.onChange(Number(val))}
            className="tw:flex tw:flex-wrap tw:gap-4 tw:!space-y-0"
          >
            {options.map((opt) => (
              <RadioField key={`${String(name)}-${opt.value}`}>
                <Radio value={String(opt.value)} />
                <Label className="tw:text-sm">{opt.label}</Label>
              </RadioField>
            ))}
          </RadioGroup>
        )}
      />
    </div>
  );
};

interface ProviderSurveyFormProps {
  id?: string;
  survey?: ProviderSurveyResponse;
}

export const ProviderSurveyForm: React.FC<ProviderSurveyFormProps> = ({ id, survey }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [surveyData, setSurveyData] = useState<ProviderSurveyResponse | null>(survey || null);
  const [showThankYou, setShowThankYou] = useState(false);

  const form = useForm<ProviderSurveyFormData>({
    resolver: zodResolver(providerSurveySchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const { control, register, handleSubmit, setValue, reset, getValues, formState } = form;

  const recommendationStatus = useWatch({ control, name: 'recommendation_status' });
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    // Only clear endorsements if this is not the initial load (i.e., user changed recommendation status)
    if (!isInitialLoad) {
      setValue('endorsements', [], { shouldDirty: true, shouldValidate: true });
    }
  }, [recommendationStatus, setValue, isInitialLoad]);

  // Fetch survey data if only id is provided
  useEffect(() => {
    const fetchSurveyData = async () => {
      if (id && !surveyData) {
        setIsLoading(true);
        setError(null);
        try {
          const data = await ProviderSurveyResponseService.providerSurveyResponseRetrieve({ id });
          setSurveyData(data);
        } catch (err) {
          console.error('Error fetching survey data:', err);
          setError('Failed to load survey data. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchSurveyData();
  }, [id, surveyData]);

  useEffect(() => {
    if (surveyData) {
      if (surveyData.current_status.code === 'COMPLETED') {
        // Don't populate form data, render ThankYou component instead
        setIsInitialLoad(false);
        return;
      }

      const formData: Partial<ProviderSurveyFormData> = {
        relationship_length: surveyData.relationship_length || null,
        relationship_type: surveyData.relationship_type || null,
        interpersonal_skills: surveyData.interpersonal_skills,
        communication: surveyData.communication,
        empathy: surveyData.empathy,
        reliability_and_dependability: surveyData.reliability_and_dependability,
        clinical_skills: surveyData.clinical_skills,
        quality_of_patient_care: surveyData.quality_of_patient_care,
        teamwork: surveyData.teamwork,
        physical_and_mental_health: surveyData.physical_and_mental_health,
        integrity: surveyData.integrity,
        honesty: surveyData.honesty,
        adherence_to_ethical_guidelines: surveyData.adherence_to_ethical_guidelines,
        recommendation_status: surveyData.recommendation_status ?? null,
        endorsements: surveyData.endorsements || [],
        comments: surveyData.comments || '',
      };

      reset(formData);
      setIsInitialLoad(false);
    }
  }, [surveyData, reset]);

  const onSubmit = handleSubmit((data) => {
    mutation.mutate(data as PatchedProviderSurveyResponse);
  });

  const mutation = useMutation({
    mutationFn: async (payload: PatchedProviderSurveyResponse) => {
      if (!id) {
        throw new Error('Missing survey id');
      }
      return ProviderSurveyResponseService.providerSurveyResponsePartialUpdate({
        id,
        requestBody: payload,
      });
    },
    onSuccess: (response) => {
      if (response.current_status.code === 'COMPLETED') {
        setShowThankYou(true);
        toast.success('Survey submitted successfully!');
      } else {
        toast.success('Progress saved');
        const current = getValues();
        reset(current);
      }
    },
    onError: (error) => {
      handleApiError(error, { messagePrefix: 'Failed to save progress' });
    },
  });

  const handleSaveProgress = () => {
    const { dirtyFields } = formState;
    const currentValues = getValues();

    const payload: Record<string, unknown> = {};

    Object.entries(dirtyFields).forEach(([key, isDirty]) => {
      if (isDirty && currentValues[key as keyof ProviderSurveyFormData] !== null) {
        const value = currentValues[key as keyof ProviderSurveyFormData];
        payload[key] = value;
      }
    });

    if (Object.keys(payload).length === 0) {
      return;
    }

    mutation.mutate(payload as PatchedProviderSurveyResponse);
  };

  const isFormValid = () => {
    const values = getValues();

    // Required fields that must have values to complete a survey response
    const requiredFields: (keyof ProviderSurveyFormData)[] = [
      'relationship_length',
      'relationship_type',
      'interpersonal_skills',
      'communication',
      'empathy',
      'reliability_and_dependability',
      'clinical_skills',
      'quality_of_patient_care',
      'teamwork',
      'physical_and_mental_health',
      'integrity',
      'honesty',
      'adherence_to_ethical_guidelines',
      'recommendation_status',
    ];

    return requiredFields.every((field) => {
      const value = values[field];
      return value !== null && value !== undefined && value !== '';
    });
  };

  // Show loading state
  if (isLoading) {
    return (
      <main className="tw:max-w-5xl tw:mx-auto tw:px-4 tw:pt-3 tw:pb-8 tw:!text-base/6">
        <div className="tw:flex tw:justify-center tw:items-center tw:py-12">
          <div className="tw:text-zinc-600">Loading survey data...</div>
        </div>
      </main>
    );
  }

  // Show error state
  if (error) {
    return (
      <main className="tw:max-w-5xl tw:mx-auto tw:px-4 tw:pt-3 tw:pb-8 tw:!text-base/6">
        <div className="tw:flex tw:justify-center tw:items-center tw:py-12">
          <div className="tw:text-stoplight-danger">{error}</div>
        </div>
      </main>
    );
  }

  // Show ProviderSurveyThankYou component if survey is completed
  if (showThankYou || (surveyData && surveyData.current_status.code === 'COMPLETED')) {
    return <ProviderSurveyThankYou provider={surveyData?.provider_survey?.provider} />;
  }

  return (
    <>
      <main className="tw:max-w-5xl tw:mx-auto tw:px-4 tw:pt-3 tw:pb-8 tw:!text-base/6 tw:dark:bg-zinc-700">
        <form onSubmit={onSubmit}>
          {/* Provider Header Card */}
          <Section>
            <div className="tw:flex tw:items-center tw:gap-4">
              <Avatar
                src={surveyData?.provider_survey.provider.profile_picture}
                initials={`${surveyData?.provider_survey.provider.preferred_name?.[0]}${surveyData?.provider_survey.provider.last_name?.[0]}`}
                alt={`${surveyData?.provider_survey.provider.prefix_name} ${surveyData?.provider_survey.provider.preferred_name} ${surveyData?.provider_survey.provider.last_name}`}
                className="tw:size-20 tw:bg-zinc-200 tw:text-zinc-400"
              />
              <div className="tw:flex-1">
                {/* TODO DARK MODE STYLES duplicating SectionHeader styles here, perhaps we need a way to pass react components to SectionHeader */}
                <div className="tw:text-2xl tw:font-semibold tw:dark:text-brand-green-1 tw:mb-1">
                  {surveyData?.provider_survey.provider.prefix_name}{' '}
                  {surveyData?.provider_survey.provider.preferred_name}{' '}
                  {surveyData?.provider_survey.provider.last_name}
                </div>
                {/* TODO get this specialty somehow */}
                {/* <Text className="tw:mb-2">{REQUESTING_PROVIDER_SPECIALTY}</Text> */}
              </div>
            </div>
            <hr className="tw:my-5 tw:!border-zinc-200 tw:dark:!border-white/10" />
            <Text className="tw:!mb-0">
              You&apos;ve been invited to provide a professional endorsement for{' '}
              {surveyData?.provider_survey.provider.prefix_name}{' '}
              {surveyData?.provider_survey.provider.preferred_name}{' '}
              {surveyData?.provider_survey.provider.last_name}. Your feedback will help maintain the
              integrity and quality of healthcare services. This survey should take less than 4
              minutes to complete.
            </Text>
          </Section>
          {/* Nature of Relationship */}
          <Section>
            <div className="tw:flex tw:items-start tw:gap-1">
              <SectionHeader headerText="Nature of Relationship" />
              <span className="tw:text-stoplight-danger">*</span>
            </div>
            <Fieldset>
              <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                <Field>
                  <Label>
                    How long have you known {surveyData?.provider_survey.provider.prefix_name}{' '}
                    {surveyData?.provider_survey.provider.preferred_name}{' '}
                    {surveyData?.provider_survey.provider.last_name}?
                  </Label>
                  <Controller
                    name="relationship_length"
                    control={control}
                    render={({ field }) => (
                      <Select
                        styles={reactSelectReset}
                        options={KNOWN_YEARS_OPTIONS}
                        placeholder={null}
                        value={KNOWN_YEARS_OPTIONS.find((o) => o.value === field.value) || null}
                        onChange={(opt) =>
                          field.onChange(opt ? (opt as { value: string }).value : null)
                        }
                        isClearable
                      />
                    )}
                  />
                </Field>
                <Field>
                  <Label>
                    In what capacity have you worked with{' '}
                    {surveyData?.provider_survey.provider.prefix_name}{' '}
                    {surveyData?.provider_survey.provider.preferred_name}{' '}
                    {surveyData?.provider_survey.provider.last_name}?
                  </Label>
                  <Controller
                    name="relationship_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        styles={reactSelectReset}
                        options={CAPACITY_OPTIONS}
                        placeholder={null}
                        value={CAPACITY_OPTIONS.find((o) => o.value === field.value) || null}
                        onChange={(opt) =>
                          field.onChange(opt ? (opt as { value: string }).value : null)
                        }
                        isClearable
                      />
                    )}
                  />
                </Field>
              </div>
              {form.watch('relationship_type') === 'other' && (
                <div className="tw:mt-4">
                  <Field>
                    <span>
                      <Input
                        {...register('other_capacity')}
                        type="text"
                        placeholder="Please specify"
                      />
                    </span>
                  </Field>
                </div>
              )}
            </Fieldset>
          </Section>

          {/* Personal Qualifications */}
          <Section>
            <div className="tw:flex tw:items-start tw:gap-1">
              <SectionHeader headerText="Personal Qualifications" />
              <span className="tw:text-stoplight-danger">*</span>
            </div>
            <Fieldset>
              <Text>
                Rate {surveyData?.provider_survey.provider.prefix_name}{' '}
                {surveyData?.provider_survey.provider.preferred_name}{' '}
                {surveyData?.provider_survey.provider.last_name} on the following areas:
              </Text>
              <div className="tw:space-y-4">
                <LikertRow
                  name="interpersonal_skills"
                  label="Interpersonal Skills"
                  control={control}
                  options={LIKERT_OPTIONS_NO_NA}
                />
                <LikertRow
                  name="communication"
                  label="Communication"
                  control={control}
                  options={LIKERT_OPTIONS_NO_NA}
                />
                <LikertRow
                  name="empathy"
                  label="Empathy"
                  control={control}
                  options={LIKERT_OPTIONS_NO_NA}
                />
                <LikertRow
                  name="reliability_and_dependability"
                  label="Reliability & Dependability"
                  control={control}
                  options={LIKERT_OPTIONS_NO_NA}
                  isLast
                />
              </div>
            </Fieldset>
          </Section>

          {/* Professional Qualifications */}
          <Section>
            <div className="tw:flex tw:items-start tw:gap-1">
              <SectionHeader headerText="Professional Qualifications" />
              <span className="tw:text-stoplight-danger">*</span>
            </div>
            <Fieldset>
              <Text>
                Rate {surveyData?.provider_survey.provider.prefix_name}{' '}
                {surveyData?.provider_survey.provider.preferred_name}{' '}
                {surveyData?.provider_survey.provider.last_name} on the following areas:
              </Text>
              <div className="tw:space-y-4">
                <LikertRow name="clinical_skills" label="Clinical Skills" control={control} />
                <LikertRow
                  name="quality_of_patient_care"
                  label="Quality of Patient Care"
                  control={control}
                />
                <LikertRow name="teamwork" label="Teamwork" control={control} />
                <LikertRow
                  name="physical_and_mental_health"
                  label="Physical & Mental Health"
                  control={control}
                  isLast
                />
              </div>
            </Fieldset>
          </Section>

          {/* Ethical Standards */}
          <Section>
            <div className="tw:flex tw:items-start tw:gap-1">
              <SectionHeader headerText="Ethical Standards" />
              <span className="tw:text-stoplight-danger">*</span>
            </div>
            <Fieldset>
              <div className="tw:space-y-4">
                <LikertRow name="integrity" label="Integrity" control={control} />
                <LikertRow name="honesty" label="Honesty" control={control} />
                <LikertRow
                  name="adherence_to_ethical_guidelines"
                  label="Adherence to Ethical Guidelines"
                  control={control}
                  isLast
                />
              </div>
            </Fieldset>
          </Section>

          {/* Recommendation Status */}
          <Section>
            <div className="tw:flex tw:items-start tw:gap-1">
              <SectionHeader headerText="Recommendation Status" />
              <span className="tw:text-stoplight-danger">*</span>
            </div>
            <Fieldset>
              <Text>
                Based on your experience, would you recommend{' '}
                {surveyData?.provider_survey.provider.prefix_name}{' '}
                {surveyData?.provider_survey.provider.preferred_name}{' '}
                {surveyData?.provider_survey.provider.last_name} for a position or continued
                practice?
              </Text>
              <Controller
                name="recommendation_status"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                    className="tw:flex tw:flex-col tw:gap-3"
                  >
                    <RadioField>
                      <Radio value={RecommendationStatusEnum.HIGHLY_RECOMMEND} />
                      <Label>Highly Recommend</Label>
                    </RadioField>
                    <RadioField>
                      <Radio value={RecommendationStatusEnum.RECOMMEND_WITH_HESITATION} />
                      <Label>Recommend with Hesitation</Label>
                    </RadioField>
                    <RadioField>
                      <Radio value={RecommendationStatusEnum.DO_NOT_RECOMMEND} />
                      <Label>Do Not Recommend</Label>
                    </RadioField>
                  </RadioGroup>
                )}
              />
            </Fieldset>

            {recommendationStatus && (
              <div className="tw:mt-6">
                <Fieldset>
                  <Text>Select 1-3 qualities that apply based on your experience:</Text>
                  <Controller
                    name="endorsements"
                    control={control}
                    render={({ field }) => {
                      const current: string[] = Array.isArray(field.value) ? field.value : [];
                      return (
                        <CheckboxGroup className="tw:flex tw:flex-col tw:gap-3">
                          {BROAD_ATTRIBUTES_OPTIONS[
                            recommendationStatus as RecommendationStatusEnum
                          ].map((opt) => {
                            const isChecked = current.includes(opt);
                            return (
                              <CheckboxField key={opt}>
                                <Checkbox
                                  value={opt}
                                  checked={isChecked}
                                  color="brandgreen"
                                  onChange={(checked: boolean) => {
                                    if (checked) {
                                      // Only allow selection if we have less than 3 endorsements
                                      if (current.length < 3) {
                                        const next = [...current, opt];
                                        field.onChange(next);
                                      }
                                    } else {
                                      // Always allow deselection
                                      const next = current.filter((v) => v !== opt);
                                      field.onChange(next);
                                    }
                                  }}
                                />
                                <Label>{opt}</Label>
                              </CheckboxField>
                            );
                          })}
                        </CheckboxGroup>
                      );
                    }}
                  />
                </Fieldset>
              </div>
            )}
          </Section>

          {/* Additional Comments */}
          <Section>
            <SectionHeader headerText="Additional Comments (Optional)" />
            <Fieldset>
              <Field>
                <Text>
                  Please share any specific examples or feedback to illustrate the above ratings or
                  any other insights about working with{' '}
                  {surveyData?.provider_survey.provider.prefix_name}{' '}
                  {surveyData?.provider_survey.provider.preferred_name}{' '}
                  {surveyData?.provider_survey.provider.last_name}.
                </Text>
                <Textarea {...register('comments')} />
              </Field>
            </Fieldset>
          </Section>

          {/* Response Visibility */}
          {/* <section className={sectionStyles}>
            <SectionHeader headerText="Response Visibility" />
            <Fieldset>
              <Text>
                Would you like {surveyData?.provider_survey.provider.prefix_name}{' '}
                {surveyData?.provider_survey.provider.preferred_name}{' '}
                {surveyData?.provider_survey.provider.last_name} to see
                your responses?
              </Text>
              <Controller
                name="response_visibility"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                    className="tw:flex tw:flex-col tw:gap-3"
                  >
                    <RadioField>
                      <Radio value="yes" />
                      <Label>Yes</Label>
                    </RadioField>
                    <RadioField>
                      <Radio value="no" />
                      <Label>No</Label>
                    </RadioField>
                  </RadioGroup>
                )}
              />
            </Fieldset>
          </section> */}

          <div className="tw:flex tw:justify-end tw:gap-4">
            <Button
              type="button"
              color="brandgreen0"
              onClick={handleSaveProgress}
              disabled={mutation.isLoading}
            >
              {mutation.isLoading
                ? isFormValid()
                  ? 'Submitting…'
                  : 'Saving…'
                : isFormValid()
                  ? 'Submit'
                  : 'Save progress'}
            </Button>
          </div>
        </form>
      </main>
    </>
  );
};

export default ProviderSurveyForm;
